import React, { useState, useEffect, useRef, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle,
  ChevronDown,
  Loader2,
  UploadCloud,
  Link as LinkIcon,
  Crop,
  Sparkles,
  Smartphone,
  Monitor,
  Search,
  HelpCircle,
  ZoomIn,
  RotateCw,
  RotateCcw,
  FlipHorizontal,
  FlipVertical,
} from "lucide-react";
import ReactCrop, {
  Crop as CropConfig,
  PixelCrop,
  centerCrop,
  makeAspectCrop,
} from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

import Dropdown, { Option } from "../../../components/customer/Dropdown";
import {
  useCreateBannerMutation,
  useUpdateBannerMutation,
  useLazyGetBannerByIdQuery,
  useLazyGetAllBrandsQuery,
} from "../../../redux/actions/Admin/ProductAdmin";
import { useLazyGetCategoryQuery } from "../../../redux/actions/Admin/dashboardAdmin";
import { useLazyGetAllPetsQuery } from "../../../redux/actions/Admin/PetsAdmin";
import { useLazyGetAllServiceQuery } from "../../../redux/actions/Admin/ServiceAdmin";
import { useUploadFileMutation } from "../../../redux/actions/Admin/ServiceAdmin";

enum BannerTargetType {
  Unknown = 0,
  Category = 1,
  Brand = 2,
  Product = 3,
  Pet = 4,
  Service = 5,
  ExternalUrl = 6,
}
interface SelectedTargetItem {
  id: number;
  name: string;
}
interface FlattenedCategory {
  id: number;
  name: string;
  breadcrumb: string;
}

const AdminCreateNewBanner: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const state = location.state;
  const currentSectionId = state?.screenSectionId || 1;
  const ScreenId = state?.screenId || 1;
  // Get raw file limits from config
  const fileConfig = state?.config || { maxSizeKb: 1000 };

  const previewConfig = useMemo(() => {
    const rawPreview = state?.config ?? state?.config ?? {};

    console.log("Raw Preview:", rawPreview);

    const config = {
      desktopWidth: rawPreview.desktopWidth,
      desktopHeight: rawPreview.desktopHeight,
      mobileWidth: rawPreview.mobileWidth,
      mobileHeight: rawPreview.mobileHeight,
    };

    console.log("Preview Config:", config);

    return config;
  }, [state]);

  // --- API Hooks ---
  const [uploadFile, { isLoading: isUploadingMedia }] = useUploadFileMutation();
  const [createBanner, { isLoading: isCreating }] = useCreateBannerMutation();
  const [updateBanner, { isLoading: isUpdating }] = useUpdateBannerMutation();
  const [triggerGetBannerById, { isFetching: isLoadingBanner }] =
    useLazyGetBannerByIdQuery();

  const isEditMode = state?.mode === "edit" && !!state?.bannerId;
  const bannerId = Number(state?.bannerId || 0);
  const isSavingData = isCreating || isUpdating || isLoadingBanner;

  const [triggerGetBrands, { data: brandList }] = useLazyGetAllBrandsQuery();
  const [triggerGetCategories, { data: nestedCategoriesRaw }] =
    useLazyGetCategoryQuery();
  const [triggerGetPets, { data: serverPetsList = [] }] =
    useLazyGetAllPetsQuery();
  const [triggerGetAllServices, { data: servicesList = [] }] =
    useLazyGetAllServiceQuery();

  // --- Form States ---
  const [title, setTitle] = useState("");
  const [subTitle, setSubTitle] = useState("");
  const [customUrl, setCustomUrl] = useState("");
  const [isSponsored, setIsSponsored] = useState(false);
  const [sponsoredText, setSponsoredText] = useState("Sponsored");
  const [uploadedDesktopUrl, setUploadedDesktopUrl] = useState<string>("");
  const [uploadedMobileUrl, setUploadedMobileUrl] = useState<string>("");
  const [displayOrder, setDisplayOrder] = useState<number>(1);
  const [isActive, setIsActive] = useState<boolean>(true);
  const [isBannerLoaded, setIsBannerLoaded] = useState(false);

  const [error, setError] = useState<string | null>(null);

  // GIF validation is handled inside the crop modal.
  // We never send an animated GIF through canvas because that would
  // reduce it to a single frame.
  const [gifDimensions, setGifDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);

  // ---------------------------------------------------------
  // Animated GIF aspect-ratio tolerance
  //
  // GIFs are uploaded in their original form so animation is
  // preserved. Since animated GIFs cannot safely go through
  // the browser canvas crop flow, we allow a reasonable
  // difference in banner shape.
  //
  // 20% tolerance allows a GIF to be somewhat taller/shorter
  // than the recommended banner while still being suitable.
  // ---------------------------------------------------------
  const GIF_ASPECT_TOLERANCE = 0.2;
  const [selectedTargetType, setSelectedTargetType] = useState<Option | null>(
    null,
  );
  const [multiSelectedTargets, setMultiSelectedTargets] = useState<
    SelectedTargetItem[]
  >([]);
  const [targetSearchQuery, setTargetSearchQuery] = useState("");

  // --- Cropper States ---
  const [activeCropTarget, setActiveCropTarget] = useState<
    "desktop" | "mobile" | null
  >(null);
  const [rawImageSrc, setRawImageSrc] = useState<string | null>(null);
  // Keep the original File so GIF uploads can be preserved without canvas conversion.
  const [rawImageFile, setRawImageFile] = useState<File | null>(null);
  const [crop, setCrop] = useState<CropConfig>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);

  // Image Manipulation States
  const [zoom, setZoom] = useState<number>(1);
  const [rotate, setRotate] = useState<number>(0);
  const [flipH, setFlipH] = useState<boolean>(false);
  const [flipV, setFlipV] = useState<boolean>(false);

  const imgRef = useRef<HTMLImageElement>(null);

  // Calculate dynamic aspect ratio based on Preview width and height rules from API
  const targetAspect = useMemo(() => {
    if (activeCropTarget === "mobile") {
      return previewConfig.mobileWidth / previewConfig.mobileHeight;
    }
    return previewConfig.desktopWidth / previewConfig.desktopHeight;
  }, [activeCropTarget, previewConfig]);

  const targetDimensions = useMemo(() => {
    return activeCropTarget === "mobile"
      ? {
          width: previewConfig.mobileWidth,
          height: previewConfig.mobileHeight,
        }
      : {
          width: previewConfig.desktopWidth,
          height: previewConfig.desktopHeight,
        };
  }, [activeCropTarget, previewConfig]);

  const currentCropDimensions = useMemo(() => {
    if (
      !completedCrop ||
      !imgRef.current ||
      rawImageFile?.type === "image/gif"
    ) {
      return null;
    }

    const image = imgRef.current;

    if (!image.width || !image.height) {
      return null;
    }

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    return {
      width: Math.round(completedCrop.width * scaleX),
      height: Math.round(completedCrop.height * scaleY),
    };
  }, [completedCrop, rawImageSrc, rawImageFile]);

  const sectionContext = useMemo(() => {
    if (ScreenId === 1) {
      return {
        rootCategoryId: 1,
        label: "Products",
        allowedTypes: [
          {
            id: "1",
            label: "Category",
            value: String(BannerTargetType.Category),
          },
          { id: "2", label: "Brand", value: String(BannerTargetType.Brand) },
          {
            id: "6",
            label: "External URL",
            value: String(BannerTargetType.ExternalUrl),
          },
        ],
      };
    } else if (ScreenId === 2) {
      return {
        rootCategoryId: 2,
        label: "Pets Marketplace",
        allowedTypes: [
          {
            id: "1",
            label: "Category",
            value: String(BannerTargetType.Category),
          },
          {
            id: "4",
            label: "Pet Profile",
            value: String(BannerTargetType.Pet),
          },
          {
            id: "6",
            label: "External URL",
            value: String(BannerTargetType.ExternalUrl),
          },
        ],
      };
    } else {
      return {
        rootCategoryId: 3,
        label: "Services",
        allowedTypes: [
          {
            id: "1",
            label: "Category",
            value: String(BannerTargetType.Category),
          },
          {
            id: "5",
            label: "Service Booking",
            value: String(BannerTargetType.Service),
          },
          {
            id: "6",
            label: "External URL",
            value: String(BannerTargetType.ExternalUrl),
          },
        ],
      };
    }
  }, [ScreenId]);
  console.log("Section Context:", sectionContext);
  useEffect(() => {
    if (!selectedTargetType) return;

    const val = Number(selectedTargetType.value);
    setTargetSearchQuery("");

    // In create mode, changing the target type should clear old selections.
    // In edit mode, keep the IDs loaded from the banner until the user changes
    // the target type manually.
    if (!isEditMode) {
      setMultiSelectedTargets([]);
    }

    if (val === BannerTargetType.Brand) triggerGetBrands();
    if (val === BannerTargetType.Category) triggerGetCategories();
    if (val === BannerTargetType.Pet) triggerGetPets({});
    if (val === BannerTargetType.Service) triggerGetAllServices();
  }, [
    selectedTargetType,
    isEditMode,
    isBannerLoaded,
    triggerGetBrands,
    triggerGetCategories,
    triggerGetPets,
    triggerGetAllServices,
  ]);

  // ---------------------------------------------------------
  // LOAD EXISTING BANNER FOR EDIT MODE
  // ---------------------------------------------------------
  useEffect(() => {
    if (!isEditMode || !bannerId) return;

    let cancelled = false;

    const loadBanner = async () => {
      try {
        setError(null);

        const response: any = await triggerGetBannerById(bannerId).unwrap();

        if (cancelled) return;

        setTitle(response?.title ?? "");
        setSubTitle(response?.subTitle ?? "");
        setUploadedDesktopUrl(response?.imageUrl ?? "");
        setUploadedMobileUrl(response?.mobileImageUrl ?? "");
        setCustomUrl(response?.customUrl ?? "");
        setDisplayOrder(Number(response?.displayOrder ?? 1));
        setIsActive(response?.isActive ?? true);
        setIsSponsored(response?.isSponsored ?? false);
        setSponsoredText(response?.sponsoredText ?? "Sponsored");

        const responseTargetType =
          response?.targetType ??
          response?.targets?.[0]?.targetType ??
          response?.redirectType ??
          BannerTargetType.Unknown;

        const targetOption = sectionContext.allowedTypes.find(
          (option: any) => Number(option.value) === Number(responseTargetType),
        );

        if (targetOption) {
          setSelectedTargetType(targetOption);
        }

        const responseTargets = Array.isArray(response?.targets)
          ? response.targets
          : [];

        const responseTargetId =
          response?.targetId ??
          responseTargets?.[0]?.targetId ??
          null;

        if (responseTargetId !== null && responseTargetId !== undefined) {
          setMultiSelectedTargets([
            {
              id: Number(responseTargetId),
              name: "",
            },
          ]);
        } else {
          setMultiSelectedTargets([]);
        }

        setIsBannerLoaded(true);
      } catch (err: any) {
        console.error("Failed to load banner", err);
        setError(
          err?.data?.Message ||
            err?.data?.message ||
            "Failed to load banner details.",
        );
      }
    };

    loadBanner();

    return () => {
      cancelled = true;
    };
  }, [
    isEditMode,
    bannerId,
    triggerGetBannerById,
    sectionContext.allowedTypes,
  ]);

  const parsedScopedCategories = useMemo<FlattenedCategory[]>(() => {
    if (!nestedCategoriesRaw || !Array.isArray(nestedCategoriesRaw)) return [];
    const flattenedList: FlattenedCategory[] = [];
    const isolatedRootNode = nestedCategoriesRaw.find(
      (c: any) => c.id === sectionContext.rootCategoryId,
    );
    if (!isolatedRootNode) return [];

    const traverseTreeNodes = (node: any, currentPath: string) => {
      const nodePath = currentPath
        ? `${currentPath} → ${node.name}`
        : node.name;
      flattenedList.push({
        id: node.id,
        name: node.name,
        breadcrumb: nodePath,
      });
      if (node.children && Array.isArray(node.children)) {
        node.children.forEach((childNode: any) =>
          traverseTreeNodes(childNode, nodePath),
        );
      }
    };

    if (isolatedRootNode.children) {
      isolatedRootNode.children.forEach((child: any) =>
        traverseTreeNodes(child, isolatedRootNode.name),
      );
    }
    return flattenedList;
  }, [nestedCategoriesRaw, sectionContext]);

  const filteredTargetItemsRoll = useMemo(() => {
    const query = targetSearchQuery.toLowerCase().trim();
    if (!selectedTargetType) return [];
    const typeVal = Number(selectedTargetType.value);

    if (typeVal === BannerTargetType.Category)
      return parsedScopedCategories.filter((c) =>
        c.breadcrumb.toLowerCase().includes(query),
      );
    if (typeVal === BannerTargetType.Brand && brandList)
      return brandList.filter((b: any) => b.name.toLowerCase().includes(query));
    if (typeVal === BannerTargetType.Pet) {
      // Pet API can return petName/name depending on the response shape.
      // Normalize both so every pet profile is displayed with its real name.
      const pets = Array.isArray(serverPetsList)
        ? serverPetsList
        : Array.isArray((serverPetsList as any)?.data)
          ? (serverPetsList as any).data
          : Array.isArray((serverPetsList as any)?.value)
            ? (serverPetsList as any).value
            : [];

      return pets.filter((p: any) => {
        const petName = String(
          p?.petName ??
            p?.name ??
            p?.PetName ??
            p?.Name ??
            "",
        ).trim();

        const breed = String(
          p?.breed ??
            p?.Breed ??
            "",
        ).trim();

        return (
          petName.toLowerCase().includes(query) ||
          breed.toLowerCase().includes(query)
        );
      });
    }
    if (typeVal === BannerTargetType.Service)
      return servicesList.filter((s: any) =>
        s.name.toLowerCase().includes(query),
      );
    return [];
  }, [
    targetSearchQuery,
    selectedTargetType,
    parsedScopedCategories,
    brandList,
    serverPetsList,
    servicesList,
  ]);

  const onSelectFile = (
    e: React.ChangeEvent<HTMLInputElement>,
    target: "desktop" | "mobile",
  ) => {
    if (e.target.files && e.target.files.length > 0) {
      setError(null);
      setActiveCropTarget(target);
      setRawImageFile(e.target.files[0]);
      setGifDimensions(null);

      setZoom(1);
      setRotate(0);
      setFlipH(false);
      setFlipV(false);

      const reader = new FileReader();
      reader.addEventListener("load", () =>
        setRawImageSrc(reader.result?.toString() || null),
      );
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height, naturalWidth, naturalHeight } = e.currentTarget;
    const naturalAspect = naturalWidth / naturalHeight;

    if (rawImageFile?.type === "image/gif") {
      setGifDimensions({
        width: naturalWidth,
        height: naturalHeight,
      });
      return;
    }

    const aspectDiff = Math.abs(naturalAspect - targetAspect);

    if (aspectDiff < 0.01) {
      // Perfect match — select 100% so the crop box covers the whole image
      setCrop({ unit: "%", x: 0, y: 0, width: 100, height: 100 });
      setCompletedCrop({ unit: "px", x: 0, y: 0, width, height });
    } else {
      setCrop(
        centerCrop(
          makeAspectCrop({ unit: "%", width: 90 }, targetAspect, width, height),
          width,
          height,
        ),
      );
    }
  };

  const handleGenerateCroppedBlob = async () => {
    if (!imgRef.current || !activeCropTarget || !rawImageFile) return;

    const image = imgRef.current;

    // ============================================================
    // GIF HANDLING
    // ============================================================
    // Canvas can only export a single rendered frame of a GIF.
    // Sending an animated GIF through canvas would therefore destroy
    // its animation and turn it into a static image.
    //
    // For GIFs we require the original file to already have the
    // correct banner aspect ratio. When it matches, we upload the
    // original File directly so the GIF animation is preserved.
    // ============================================================
    if (rawImageFile.type === "image/gif") {
      const naturalAspect = image.naturalWidth / image.naturalHeight;

      const aspectDiff = Math.abs(naturalAspect - targetAspect);
      const aspectDifferencePercent = (aspectDiff / targetAspect) * 100;

      // GIFs are not cropped/resized in canvas. Allow a small shape
      // difference because the original GIF is preserved unchanged.
      if (aspectDifferencePercent > GIF_ASPECT_TOLERANCE * 100) {
        setError(
          `This GIF is too different from the recommended banner shape. ` +
            `Your GIF is ${naturalAspect.toFixed(
              2,
            )}:1, while the recommended shape is ${targetAspect.toFixed(2)}:1,` +
            ` so please use a wider or shorter GIF.`,
        );
        return;
      }

      // A valid GIF must not carry a previous page-level error.
      setError(null);

      try {
        if (rawImageFile.size / 1024 > fileConfig.maxSizeKb) {
          setError(`File size exceeds limit (${fileConfig.maxSizeKb}KB).`);
          return;
        }

        const response = await uploadFile({
          File: rawImageFile,
          FolderName: "Marketing/CampaignBanners",
        }).unwrap();

        if (activeCropTarget === "desktop") {
          setUploadedDesktopUrl(response.fileUrl);
        }

        if (activeCropTarget === "mobile") {
          setUploadedMobileUrl(response.fileUrl);
        }

        setRawImageSrc(null);
        setRawImageFile(null);
        setActiveCropTarget(null);
        setCrop(undefined);
        setCompletedCrop(null);
      } catch (err) {
        console.error("GIF upload failed:", err);
        setError("Failed to upload GIF.");
      }

      return;
    }

    // ============================================================
    // STATIC IMAGE HANDLING
    // ============================================================
    // JPG / PNG / WebP continue through canvas cropping. The canvas
    // output uses the exact dimensions supplied by BannerLayoutHelper.
    // ============================================================
    const canvas = document.createElement("canvas");

    const targetW =
      activeCropTarget === "mobile"
        ? previewConfig.mobileWidth
        : previewConfig.desktopWidth;
    const targetH =
      activeCropTarget === "mobile"
        ? previewConfig.mobileHeight
        : previewConfig.desktopHeight;

    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext("2d");

    if (ctx) {
      ctx.save();
      ctx.translate(targetW / 2, targetH / 2);
      ctx.rotate((rotate * Math.PI) / 180);
      ctx.scale(flipH ? -1 : 1, flipV ? -1 : 1);
      ctx.translate(-targetW / 2, -targetH / 2);

      const naturalW = image.naturalWidth;
      const naturalH = image.naturalHeight;
      const naturalAspect = naturalW / naturalH;
      const targetAspectRatio = targetW / targetH;
      const aspectDiff = Math.abs(naturalAspect - targetAspectRatio);

      let sourceX: number,
        sourceY: number,
        sourceWidth: number,
        sourceHeight: number;

      // If image aspect matches target (within 1% tolerance) OR no completed crop, use full image
      if (aspectDiff < 0.01 || !completedCrop) {
        sourceX = 0;
        sourceY = 0;
        sourceWidth = naturalW;
        sourceHeight = naturalH;
      } else {
        const scaleX = naturalW / image.width;
        const scaleY = naturalH / image.height;
        sourceX = completedCrop.x * scaleX;
        sourceY = completedCrop.y * scaleY;
        sourceWidth = completedCrop.width * scaleX;
        sourceHeight = completedCrop.height * scaleY;
      }

      ctx.drawImage(
        image,
        sourceX,
        sourceY,
        sourceWidth,
        sourceHeight,
        0,
        0,
        targetW,
        targetH,
      );
      ctx.restore();

      canvas.toBlob(
        async (blob) => {
          if (!blob) return;
          if (blob.size / 1024 > fileConfig.maxSizeKb) {
            setError(`File size exceeds limit (${fileConfig.maxSizeKb}KB).`);
            return;
          }

          try {
            const compiledFile = new File(
              [blob],
              `${activeCropTarget}-campaign.jpg`,
              { type: "image/jpeg" },
            );
            const response = await uploadFile({
              File: compiledFile,
              FolderName: "Marketing/CampaignBanners",
            }).unwrap();

            if (activeCropTarget === "desktop")
              setUploadedDesktopUrl(response.fileUrl);
            if (activeCropTarget === "mobile")
              setUploadedMobileUrl(response.fileUrl);

            setRawImageSrc(null);
            setRawImageFile(null);
            setGifDimensions(null);
            setActiveCropTarget(null);
            setCrop(undefined);
            setCompletedCrop(null);
            setError(null);
          } catch (err) {
            setError("Failed to upload image.");
          }
        },
        "image/jpeg",
        0.92,
      );
    }
  };

  const handleToggleSelectTargetItem = (id: number, name: string) => {
    const typeVal = Number(selectedTargetType?.value);
    if (typeVal === BannerTargetType.Category) {
      setMultiSelectedTargets((prev) =>
        prev.some((t) => t.id === id)
          ? prev.filter((t) => t.id !== id)
          : [...prev, { id, name }],
      );
    } else {
      setMultiSelectedTargets((prev) =>
        prev.some((t) => t.id === id) ? [] : [{ id, name }],
      );
    }
  };

  const handleFormSubmissionSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !uploadedDesktopUrl ||
      !selectedTargetType ||
      isCreating ||
      isUpdating ||
      isLoadingBanner
    ) {
      setError("Please upload a desktop image and select a redirect target.");
      return;
    }

    const typeEnumValue = Number(selectedTargetType.value);

    const finalPayload = {
      screenSectionId: currentSectionId,
      title: title.trim() || "New Campaign Launch",
      subTitle: subTitle.trim(),
      imageUrl: uploadedDesktopUrl,
      mobileImageUrl: uploadedMobileUrl || uploadedDesktopUrl,
      displayOrder,
      isActive,
      isSponsored,
      sponsoredText: isSponsored
        ? sponsoredText.trim() || "Sponsored"
        : null,
      targetType: typeEnumValue,
      targetId:
        multiSelectedTargets.length > 0
          ? multiSelectedTargets[0].id
          : null,
      customUrl:
        typeEnumValue === BannerTargetType.ExternalUrl
          ? customUrl.trim()
          : null,
      targets: multiSelectedTargets.map((t) => ({
        targetId: t.id,
        targetType: typeEnumValue,
      })),
    };

    try {
      if (isEditMode) {
        await updateBanner({
          id: bannerId,
          data: finalPayload,
        }).unwrap();
      } else {
        await createBanner(finalPayload).unwrap();
      }

      navigate(-1);
    } catch (err: any) {
      console.error("Failed to save banner", err);

      setError(
        err?.data?.Message ||
          err?.data?.message ||
          (isEditMode
            ? "Failed to update banner."
            : "Failed to create banner."),
      );
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 space-y-6 pb-24 pt-4 text-left antialiased">
      {/* BACK BUTTON */}
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-2 text-xs font-bold text-slate-600 bg-white border border-slate-200 px-4 py-2 rounded-xl hover:bg-slate-50 transition shadow-2xs cursor-pointer"
      >
        <ArrowLeft size={14} className="text-[#2F5E2E] stroke-[3]" />
        <span>Back</span>
      </button>

      {/* IMAGE CROP MODAL */}
      {rawImageSrc && activeCropTarget && (
        <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-md z-50 flex items-center justify-center p-4">
          <div className="bg-white border border-slate-200 shadow-2xl rounded-3xl max-w-4xl w-full flex flex-col max-h-[90vh] overflow-hidden">
            {/* MODAL HEADER */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-slate-100 p-5 gap-3 bg-slate-50/50">
              <div className="space-y-0.5">
                <h3 className="text-sm font-black text-slate-900 flex items-center gap-1.5 capitalize">
                  <Crop size={16} className="text-[#2F5E2E]" /> Crop Image (
                  {activeCropTarget})
                </h3>
                <p className="text-[10px] text-slate-400 font-semibold font-mono">
                  Dimensions:{" "}
                  {activeCropTarget === "mobile"
                    ? `${previewConfig.mobileWidth}x${previewConfig.mobileHeight}`
                    : `${previewConfig.desktopWidth}x${previewConfig.desktopHeight}`}
                  px
                </p>
              </div>
              <div className="px-3 py-1 bg-emerald-50 text-[#2F5E2E] rounded-lg text-[10px] font-mono font-black border border-emerald-100/80">
                Aspect Ratio Locked
              </div>
            </div>

            {/* WORK AREA */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-slate-50/50">
              {rawImageFile?.type === "image/gif" ? (
                <>
                  {/* GIF PREVIEW - NEVER PASS ANIMATED GIF THROUGH CANVAS */}
                  <div className="border border-slate-200 bg-slate-900 rounded-2xl p-4 flex items-center justify-center min-h-[260px] max-h-[45vh] overflow-hidden shadow-inner">
                    <img
                      ref={imgRef}
                      src={rawImageSrc}
                      alt="Animated GIF preview"
                      onLoad={onImageLoad}
                      className="max-w-full max-h-[38vh] object-contain"
                    />
                  </div>

                  {(() => {
                    const actualAspect = gifDimensions
                      ? gifDimensions.width / gifDimensions.height
                      : 0;

                    const differencePercent = gifDimensions
                      ? (Math.abs(actualAspect - targetAspect) / targetAspect) *
                        100
                      : 100;

                    const isGifShapeValid =
                      !!gifDimensions &&
                      differencePercent <= GIF_ASPECT_TOLERANCE * 100;

                    return (
                      <div
                        className={`rounded-2xl border p-5 ${
                          isGifShapeValid
                            ? "bg-emerald-50 border-emerald-200"
                            : "bg-amber-50 border-amber-200"
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <span
                            className={`text-lg ${
                              isGifShapeValid
                                ? "text-emerald-600"
                                : "text-amber-600"
                            }`}
                          >
                            {isGifShapeValid ? "✓" : "⚠"}
                          </span>

                          <div className="flex-1">
                            <h4
                              className={`text-sm font-black ${
                                isGifShapeValid
                                  ? "text-emerald-800"
                                  : "text-amber-800"
                              }`}
                            >
                              {isGifShapeValid
                                ? "This GIF is ready to upload"
                                : "This GIF is too different from the recommended shape"}
                            </h4>

                            <p
                              className={`text-xs leading-5 mt-1 ${
                                isGifShapeValid
                                  ? "text-emerald-700"
                                  : "text-amber-700"
                              }`}
                            >
                              Animated GIFs cannot be cropped here without
                              removing the animation. We keep the original GIF
                              and only check that its shape is reasonably close
                              to the banner shape.
                            </p>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                          <div className="bg-white/80 rounded-xl p-3 border border-white">
                            <p className="text-[9px] font-black uppercase tracking-wide text-slate-400">
                              Your GIF
                            </p>
                            <p className="text-sm font-black text-slate-800 mt-1 font-mono">
                              {gifDimensions
                                ? `${gifDimensions.width} × ${gifDimensions.height} px`
                                : "Reading dimensions..."}
                            </p>
                          </div>

                          <div className="bg-white/80 rounded-xl p-3 border border-white">
                            <p className="text-[9px] font-black uppercase tracking-wide text-slate-400">
                              Required Banner Size
                            </p>
                            <p className="text-sm font-black text-slate-800 mt-1 font-mono">
                              {targetDimensions.width} ×{" "}
                              {targetDimensions.height} px
                            </p>
                          </div>
                        </div>

                        <p className="text-[10px] text-slate-500 mt-3">
                          <strong>Shape tolerance:</strong> ±20%. The exact
                          pixel dimensions do not have to match because the
                          original animated GIF is preserved.
                        </p>

                        {error && !isGifShapeValid && (
                          <p className="text-[11px] font-bold text-amber-800 mt-3">
                            {error}
                          </p>
                        )}

                        <div className="bg-white/80 rounded-xl p-3 text-[11px] text-slate-600 leading-5 mt-3">
                          <strong className="text-slate-800">Tip:</strong> Use a
                          wide and short GIF similar to the banner shape. You do
                          not need to create the exact pixel dimensions.
                        </div>
                      </div>
                    );
                  })()}
                </>
              ) : (
                <>
                  {/* STATIC IMAGE CROPPER - ORIGINAL FLOW RETAINED */}
                  <div className="border border-slate-200 bg-slate-900 rounded-2xl p-4 flex items-center justify-center min-h-[300px] max-h-[45vh] overflow-auto relative group shadow-inner">
                    <ReactCrop
                      crop={crop}
                      onChange={(c) => setCrop(c)}
                      onComplete={(c) => setCompletedCrop(c)}
                      aspect={targetAspect}
                      locked
                      className="max-w-full max-h-[40vh]"
                    >
                      <img
                        ref={imgRef}
                        src={rawImageSrc}
                        alt="Crop source"
                        onLoad={onImageLoad}
                        style={{
                          transform: `scaleX(${flipH ? -1 : 1}) scaleY(${
                            flipV ? -1 : 1
                          }) rotate(${rotate}deg) scale(${zoom})`,
                          transition: "transform 0.15s ease-out",
                        }}
                        className="max-w-full max-h-[38vh] object-contain"
                      />
                    </ReactCrop>
                  </div>

                  {/* CROP SIZE INFORMATION */}
                  <div className="bg-white rounded-2xl p-4 border border-slate-200/80 space-y-4 shadow-3xs">
                    <div>
                      <h4 className="text-[11px] font-black uppercase text-slate-400 tracking-wider mb-3">
                        Crop
                      </h4>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
                          <p className="text-[9px] font-black uppercase tracking-wide text-slate-400">
                            Current Crop Size
                          </p>
                          <p className="text-sm font-black text-slate-800 mt-1 font-mono">
                            {currentCropDimensions
                              ? `${currentCropDimensions.width} × ${currentCropDimensions.height} px`
                              : "Select an area to see size"}
                          </p>
                        </div>

                        <div className="rounded-xl border border-emerald-100 bg-emerald-50/60 px-3 py-2.5">
                          <p className="text-[9px] font-black uppercase tracking-wide text-slate-400">
                            Final Backend Size
                          </p>
                          <p className="text-sm font-black text-[#2F5E2E] mt-1 font-mono">
                            {targetDimensions.width} × {targetDimensions.height}{" "}
                            px
                          </p>
                        </div>
                      </div>

                      <p className="text-[10px] text-slate-400 mt-2">
                        Drag the crop area to choose which part of the image to
                        keep. The existing canvas will generate the final
                        backend size when you save.
                      </p>
                    </div>

                    {/* EXISTING TOOLS */}
                    <div className="pt-3 border-t border-slate-100">
                      <h4 className="text-[11px] font-black uppercase text-slate-400 tracking-wider mb-3">
                        Image Adjustments
                      </h4>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                        <div className="space-y-1.5">
                          <div className="flex justify-between items-center text-[11px] text-slate-500 font-bold">
                            <span className="flex items-center gap-1 font-black uppercase">
                              <ZoomIn size={12} /> Zoom
                            </span>
                            <span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded text-slate-700">
                              {zoom.toFixed(2)}x
                            </span>
                          </div>

                          <input
                            type="range"
                            min="1"
                            max="3"
                            step="0.05"
                            value={zoom}
                            onChange={(e) => setZoom(Number(e.target.value))}
                            className="w-full accent-[#2F5E2E] bg-slate-100 rounded-lg h-2 appearance-none cursor-pointer"
                          />
                        </div>

                        <div className="flex flex-wrap items-center gap-4">
                          <div className="space-y-1">
                            <span className="block text-[10px] font-black text-slate-400 uppercase tracking-wide">
                              Rotate
                            </span>
                            <div className="inline-flex rounded-xl border border-slate-200 overflow-hidden bg-slate-50">
                              <button
                                type="button"
                                onClick={() => setRotate((r) => r - 90)}
                                className="p-2 hover:bg-white text-slate-600 border-r border-slate-200 transition"
                                title="Rotate Left 90°"
                              >
                                <RotateCcw size={14} />
                              </button>
                              <button
                                type="button"
                                onClick={() => setRotate((r) => r + 90)}
                                className="p-2 hover:bg-white text-slate-600 transition"
                                title="Rotate Right 90°"
                              >
                                <RotateCw size={14} />
                              </button>
                            </div>
                          </div>

                          <div className="space-y-1">
                            <span className="block text-[10px] font-black text-slate-400 uppercase tracking-wide">
                              Flip
                            </span>
                            <div className="inline-flex rounded-xl border border-slate-200 overflow-hidden bg-slate-50">
                              <button
                                type="button"
                                onClick={() => setFlipH((f) => !f)}
                                className={`p-2 transition border-r border-slate-200 ${
                                  flipH
                                    ? "bg-emerald-50 text-[#2F5E2E]"
                                    : "hover:bg-white text-slate-600"
                                }`}
                                title="Flip Horizontal"
                              >
                                <FlipHorizontal size={14} />
                              </button>
                              <button
                                type="button"
                                onClick={() => setFlipV((f) => !f)}
                                className={`p-2 transition ${
                                  flipV
                                    ? "bg-emerald-50 text-[#2F5E2E]"
                                    : "hover:bg-white text-slate-600"
                                }`}
                                title="Flip Vertical"
                              >
                                <FlipVertical size={14} />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* MODAL FOOTER ACTIONS */}
            <div className="flex gap-3 justify-end p-4 border-t border-slate-100 bg-white">
              <button
                type="button"
                onClick={() => {
                  // Modal-only validation must never remain visible
                  // on the parent banner form after closing the modal.
                  setError(null);
                  setRawImageSrc(null);
                  setRawImageFile(null);
                  setGifDimensions(null);
                  setActiveCropTarget(null);
                  setCrop(undefined);
                  setCompletedCrop(null);
                }}
                className="px-5 h-9 font-bold border border-slate-200 text-slate-500 rounded-xl hover:bg-slate-50 text-xs transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleGenerateCroppedBlob}
                disabled={
                  isUploadingMedia ||
                  (rawImageFile?.type === "image/gif" &&
                    (!gifDimensions ||
                      Math.abs(
                        gifDimensions.width / gifDimensions.height -
                          targetAspect,
                      ) /
                        targetAspect >
                        GIF_ASPECT_TOLERANCE))
                }
                className="px-5 h-9 font-black bg-[#2F5E2E] hover:bg-[#1f3f1e] text-white text-xs rounded-xl flex items-center gap-1.5 transition shadow-2xs disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {isUploadingMedia ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <CheckCircle size={14} />
                )}{" "}
                {rawImageFile?.type === "image/gif"
                  ? "Upload GIF"
                  : "Save Crop"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MAIN CONFIGURATION FORM */}
      {isEditMode && isLoadingBanner && !isBannerLoaded && (
        <div className="bg-emerald-50 border border-emerald-100 text-[#2F5E2E] rounded-2xl px-4 py-3 text-xs font-bold flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" />
          Loading banner details...
        </div>
      )}

      <form
        onSubmit={handleFormSubmissionSubmit}
        className="bg-white border border-slate-200/80 rounded-3xl p-6 lg:p-8 space-y-6 shadow-3xs relative overflow-hidden"
      >
        <div>
          <h2 className="text-xl font-black text-slate-900 tracking-tight flex items-center gap-1.5">
            <Sparkles size={18} className="text-[#2F5E2E]" />{" "}
            {isEditMode ? "Edit Banner" : "New Banner Config"}
          </h2>
          <p className="text-[11px] text-slate-400 font-semibold mt-0.5">
            Scope:{" "}
            <span className="font-bold text-slate-700 underline decoration-[#6DBE45]">
              {sectionContext.label}
            </span>
          </p>
        </div>

        {/* ---------------------------------------------------------
    BANNER BASIC INFORMATION
---------------------------------------------------------- */}

        {/* Row 1: Campaign Title + Subtitle */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Campaign Title */}
          <div className="space-y-1">
            <label className="block text-[11px] font-black text-slate-500 uppercase tracking-wide">
              Campaign Title
            </label>

            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="E.g., Special Treats Festival"
              className="
        w-full
        text-xs
        font-semibold
        p-3
        bg-slate-50/40
        border
        border-slate-200
        rounded-xl
        focus:outline-none
        focus:border-[#6DBE45]
        transition
      "
            />
          </div>

          {/* Subtitle / Offer Text */}
          <div className="space-y-1">
            <label className="block text-[11px] font-black text-slate-500 uppercase tracking-wide">
              Subtitle / Offer Text
            </label>

            <input
              type="text"
              value={subTitle}
              onChange={(e) => setSubTitle(e.target.value)}
              placeholder="E.g., Flat 10% off on checkout"
              className="
        w-full
        text-xs
        font-semibold
        p-3
        bg-slate-50/40
        border
        border-slate-200
        rounded-xl
        focus:outline-none
        focus:border-[#6DBE45]
        transition
      "
            />
          </div>
        </div>

        {/* ---------------------------------------------------------
    SPONSORED INFORMATION
---------------------------------------------------------- */}

        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Sponsored Label */}
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <div>
                <label className="block text-[11px] font-black text-slate-500 uppercase tracking-wide">
                  Sponsored Label
                </label>

                <p className="text-[10px] text-slate-400 font-medium mt-1">
                  Display a small label on the customer banner.
                </p>
              </div>

              {/* Toggle */}
              <button
                type="button"
                onClick={() => setIsSponsored((prev) => !prev)}
                className={`
          relative
          inline-flex
          h-6
          w-11
          shrink-0
          items-center
          rounded-full
          transition-colors
          duration-200
          focus:outline-none
          ${isSponsored ? "bg-[#2F5E2E]" : "bg-slate-200"}
        `}
                aria-pressed={isSponsored}
                aria-label="Toggle sponsored label"
              >
                <span
                  className={`
            inline-block
            h-4
            w-4
            rounded-full
            bg-white
            shadow-sm
            transition-transform
            duration-200
            ${isSponsored ? "translate-x-6" : "translate-x-1"}
          `}
                />
              </button>
            </div>
          </div>

          {/* Label Text */}
          <div className="space-y-1">
            <label
              className={`
        block
        text-[11px]
        font-black
        uppercase
        tracking-wide
        ${isSponsored ? "text-slate-500" : "text-slate-300"}
      `}
            >
              Label Text
            </label>

            <input
              type="text"
              value={sponsoredText}
              onChange={(e) => setSponsoredText(e.target.value)}
              placeholder="Sponsored"
              maxLength={50}
              disabled={!isSponsored}
              className={`
        w-full
        text-xs
        font-semibold
        p-3
        border
        rounded-xl
        focus:outline-none
        transition
        ${
          isSponsored
            ? `
              bg-slate-50/40
              border-slate-200
              focus:border-[#6DBE45]
            `
            : `
              bg-slate-50
              border-slate-100
              text-slate-300
              cursor-not-allowed
            `
        }
      `}
            />

            <p
              className={`
        text-[10px]
        ${isSponsored ? "text-slate-400" : "text-slate-300"}
      `}
            >
              {isSponsored
                ? "This text will appear on the banner."
                : "Enable Sponsored Label to edit this text."}
            </p>
          </div>
        </div>

        {/* UPLOADS */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 border-t border-b border-slate-100 py-6">
          {/* DESKTOP */}
          <div className="space-y-2">
            <label className="text-[11px] font-black text-slate-500 uppercase tracking-wide flex items-center gap-1">
              <Monitor size={14} className="text-slate-400" /> Desktop Image
              (Required)
            </label>
            {!uploadedDesktopUrl ? (
              <div className="relative border-2 border-dashed border-slate-200 rounded-2xl p-6 bg-slate-50/30 hover:bg-slate-50 hover:border-[#6DBE45] flex flex-col items-center justify-center text-center transition min-h-[140px]">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={(e) => onSelectFile(e, "desktop")}
                  className="absolute inset-0 opacity-0 cursor-pointer z-10"
                />
                <UploadCloud size={24} className="text-slate-400 mb-1" />
                <span className="text-xs font-bold text-slate-600">
                  Upload Image
                </span>
                <span className="text-[10px] text-slate-400 font-medium mt-0.5">
                  Required size: {previewConfig.desktopWidth}x
                  {previewConfig.desktopHeight}px
                </span>
              </div>
            ) : (
              <div className="group border border-slate-200 rounded-2xl overflow-hidden relative bg-slate-900 aspect-video flex items-center justify-center shadow-2xs">
                <img
                  src={uploadedDesktopUrl}
                  alt="Desktop Preview"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-slate-900/50 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                  <label className="bg-white text-slate-800 text-[10px] font-black px-3 py-1.5 rounded-lg shadow-sm cursor-pointer border border-slate-200">
                    Change Image{" "}
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      className="hidden"
                      onChange={(e) => onSelectFile(e, "desktop")}
                    />
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* MOBILE */}
          <div className="space-y-2">
            <label className="text-[11px] font-black text-slate-500 uppercase tracking-wide flex items-center gap-1">
              <Smartphone size={14} className="text-slate-400" /> Mobile Image
              (Optional)
            </label>
            {!uploadedMobileUrl ? (
              <div className="relative border-2 border-dashed border-slate-200 rounded-2xl p-6 bg-slate-50/30 hover:bg-slate-50 hover:border-[#6DBE45] flex flex-col items-center justify-center text-center transition min-h-[140px]">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={(e) => onSelectFile(e, "mobile")}
                  className="absolute inset-0 opacity-0 cursor-pointer z-10"
                />
                <UploadCloud size={24} className="text-slate-400 mb-1" />
                <span className="text-xs font-bold text-slate-600">
                  Upload Image
                </span>
                <span className="text-[10px] text-slate-400 font-medium mt-0.5">
                  Required size: {previewConfig.mobileWidth}x
                  {previewConfig.mobileHeight}px
                </span>
              </div>
            ) : (
              <div className="group border border-slate-200 rounded-2xl overflow-hidden relative bg-slate-900 aspect-video flex items-center justify-center shadow-2xs">
                <img
                  src={uploadedMobileUrl}
                  alt="Mobile Preview"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-slate-900/50 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                  <label className="bg-white text-slate-800 text-[10px] font-black px-3 py-1.5 rounded-lg shadow-sm cursor-pointer border border-slate-200">
                    Change Image{" "}
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      className="hidden"
                      onChange={(e) => onSelectFile(e, "mobile")}
                    />
                  </label>
                </div>
              </div>
            )}
          </div>
        </div>

        {isEditMode && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="space-y-1">
              <label className="block text-[11px] font-black text-slate-500 uppercase tracking-wide">
                Display Order
              </label>
              <input
                type="number"
                min={1}
                value={displayOrder}
                onChange={(e) =>
                  setDisplayOrder(Math.max(1, Number(e.target.value) || 1))
                }
                className="w-full text-xs font-semibold p-3 bg-slate-50/40 border border-slate-200 rounded-xl focus:outline-none focus:border-[#6DBE45]"
              />
            </div>

            <div className="space-y-1">
              <label className="block text-[11px] font-black text-slate-500 uppercase tracking-wide">
                Banner Status
              </label>
              <button
                type="button"
                onClick={() => setIsActive((prev) => !prev)}
                className={`w-full p-3 rounded-xl border text-xs font-black text-left transition ${
                  isActive
                    ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                    : "bg-slate-50 border-slate-200 text-slate-500"
                }`}
              >
                {isActive ? "Active / Live" : "Inactive / Paused"}
              </button>
            </div>
          </div>
        )}

        {error &&
          !error.startsWith(
            "This GIF is too different from the recommended banner shape.",
          ) && (
            <div className="p-3 bg-rose-50 border border-rose-100 text-rose-700 text-xs font-bold rounded-xl">
              ⚠️ {error}
            </div>
          )}

        {/* ROUTING */}
        <div className="space-y-5 pt-2">
          <div className="w-full max-w-sm space-y-1">
            <label className="block text-[11px] font-black text-slate-500 uppercase tracking-wide">
              Redirect Target Action
            </label>
            <Dropdown
              options={sectionContext.allowedTypes}
              value={selectedTargetType}
              onChange={(opt: any) => setSelectedTargetType(opt)}
              showLabel={false}
              dropdownIcon={<ChevronDown size={14} />}
              innerClassName="bg-slate-50 border-slate-200 text-xs font-bold py-2.5"
            />
          </div>

          {/* DYNAMIC LIST TARGETS */}
          {selectedTargetType &&
            Number(selectedTargetType.value) !==
              BannerTargetType.ExternalUrl && (
              <div className="p-5 bg-[#FAFBF8] border rounded-2xl border-slate-200/80 max-w-3xl space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between border-b border-slate-200/60 pb-2.5 gap-2">
                  <span className="text-[11px] font-black text-slate-700 uppercase tracking-wider flex items-center gap-1">
                    <HelpCircle size={13} className="text-[#2F5E2E]" />
                    Select Target Link
                  </span>
                  <div className="relative w-full sm:max-w-xs">
                    <Search
                      size={12}
                      className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"
                    />
                    <input
                      type="text"
                      value={targetSearchQuery}
                      onChange={(e) => setTargetSearchQuery(e.target.value)}
                      placeholder="Search..."
                      className="w-full text-[11px] font-medium pl-8 pr-2.5 py-1.5 border border-slate-200 bg-white rounded-lg focus:outline-none focus:border-[#6DBE45]"
                    />
                  </div>
                </div>

                <div className="max-h-52 overflow-y-auto grid grid-cols-1 gap-1.5 p-1">
                  {filteredTargetItemsRoll.length === 0 ? (
                    <div className="text-center text-slate-400 italic text-xs py-6 font-medium">
                      No matches found.
                    </div>
                  ) : (
                    filteredTargetItemsRoll.map((item: any) => {
                      const itemId =
                        item.id ??
                        item.petId ??
                        item.Id ??
                        item.PetId;

                      const petName = String(
                        item?.petName ??
                          item?.name ??
                          item?.PetName ??
                          item?.Name ??
                          "",
                      ).trim();

                      const breed = String(
                        item?.breed ??
                          item?.Breed ??
                          "",
                      ).trim();

                      const isChecked = multiSelectedTargets.some(
                        (t) => t.id === itemId,
                      );

                      // For Pet Profile, show the actual pet profile name.
                      // "Target Node" is only a fallback for other target types.
                      const titleLabel =
                        Number(selectedTargetType?.value) ===
                        BannerTargetType.Pet
                          ? petName || `Pet #${itemId}`
                          : item.breadcrumb || item.name || "Target Node";

                      return (
                        <button
                          key={itemId}
                          type="button"
                          onClick={() =>
                            handleToggleSelectTargetItem(
                              itemId,
                              titleLabel,
                            )
                          }
                          className={`w-full text-left p-2.5 rounded-xl border text-[11px] font-bold tracking-tight transition flex items-center justify-between cursor-pointer ${
                            isChecked
                              ? "bg-emerald-600 border-emerald-600 text-white shadow-xs"
                              : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                          }`}
                        >
                          <span className="truncate">
                            {titleLabel}
                            {Number(selectedTargetType?.value) ===
                              BannerTargetType.Pet &&
                              breed && (
                                <span className="ml-2 text-[10px] opacity-70 font-medium">
                                  ({breed})
                                </span>
                              )}
                          </span>
                          {isChecked && (
                            <CheckCircle
                              size={12}
                              className="text-white shrink-0 ml-2"
                            />
                          )}
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            )}

          {/* EXTERNAL LINK */}
          {selectedTargetType &&
            Number(selectedTargetType.value) ===
              BannerTargetType.ExternalUrl && (
              <div className="p-4 bg-slate-50/50 border rounded-2xl border-slate-200 max-w-md text-xs space-y-1.5">
                <label className="font-black text-slate-700 uppercase tracking-wide flex items-center gap-1.5">
                  <LinkIcon size={13} className="text-[#2F5E2E]" /> Target Link
                  URL
                </label>
                <input
                  type="url"
                  value={customUrl}
                  onChange={(e) => setCustomUrl(e.target.value)}
                  placeholder="https://example.com/campaign"
                  className="w-full text-xs font-semibold p-3 bg-white border border-slate-200 rounded-xl focus:outline-none focus:border-[#6DBE45]"
                  required
                />
              </div>
            )}

          {/* CTAS */}
          <div className="flex justify-end gap-3 pt-5 border-t border-slate-100">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-6 h-10 border border-slate-200 text-slate-500 hover:bg-slate-50 font-bold text-xs rounded-xl transition"
            >
              Discard
            </button>
            <button
              type="submit"
              disabled={
                isSavingData || !uploadedDesktopUrl || !selectedTargetType
              }
              className="px-6 h-10 bg-[#2F5E2E] hover:bg-[#1f3f1e] text-white font-black text-xs rounded-xl flex items-center justify-center gap-2 shadow-sm transition disabled:opacity-40"
            >
              {isSavingData ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <CheckCircle size={14} />
              )}
              {isEditMode ? "Update Banner" : "Deploy Changes"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AdminCreateNewBanner;
